<template>
    <div class="relative min-h-screen bg-gradient-to-br from-gray-100 to-blue-50">

        <!-- Toggle Filter Button (top-left corner) -->
        <button @click="toggleFilter"
            class="fixed top-24 left-4 z-50 bg-white shadow-md rounded-full p-2 hover:bg-gray-100 h-12 w-12 flex flex-col justify-center items-center">
            <font-awesome-icon :icon="['fas', 'filter']" class="text-gray-700 text-xl" />
        </button>

        <!-- Sliding Sidebar Filter -->
        <transition name="slide">
            <aside v-show="showFilter"
                class="fixed top-0 left-0 h-full w-64 bg-white shadow-lg z-40 flex flex-col border-r">
                <div class="p-6 border-b">
                    <h2 class="text-xl font-bold text-gray-800 w-full flex items-center justify-center pt-20 mt-1">
                        <font-awesome-icon :icon="['fas', 'sliders-h']" class="mr-2 text-blue-600" />
                        Filters
                    </h2>
                </div>
                <div class="flex-1 overflow-y-auto px-6 py-4 space-y-6">
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Entities</label>
                        <multiselect v-model="filters.entities" :options="entityOptions" :multiple="true"
                            :close-on-select="false" placeholder="Select entities" track-by="id" label="name">
                        </multiselect>
                    </div>
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Campuses</label>
                        <multiselect v-model="filters.campuses" :options="campusOptionsFiltered" :multiple="true"
                            :close-on-select="false" placeholder="Select campuses" track-by="id" label="name">
                        </multiselect>
                    </div>
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">User Types</label>
                        <multiselect v-model="filters.userTypes" :options="userTypeOptions" :multiple="true"
                            :close-on-select="false" placeholder="Select user types" track-by="id" label="name">
                        </multiselect>
                    </div>
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Departments</label>
                        <multiselect v-model="filters.departments" :options="departmentOptions" :multiple="true"
                            :close-on-select="false" placeholder="Select departments" track-by="id" label="name">
                        </multiselect>
                    </div>
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Person</label>
                        <multiselect v-model="filters.person" :options="personOptions" :multiple="false"
                            :close-on-select="true" placeholder="Select person" track-by="id" label="name">
                        </multiselect>
                    </div>
                </div>

                <!-- Apply Button -->
                <div class="p-6 flex justify-center space-x-6 border-t">
                    <button @click.stop="clearFilters"
                        class="bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm font-semibold">
                        Clear
                    </button>
                    <button @click.stop="applyFilters"
                        class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm font-semibold">
                        Apply
                    </button>
                </div>
            </aside>
        </transition>

        <div class="h-full container mx-auto">
            <div class="flex flex-col h-full w-full p-0 sm:p-8">
                <!-- Breadcrumb -->
                <nav class="text-sm text-gray-600 mb-6 flex pt-6 sm:pt-0 pl-4 sm:pl-0">
                    <router-link to="/dashboard"
                        class="hover:text-blue-700 transition font-medium">Dashboard</router-link>
                    <span class="mx-1">/</span>
                    <div v-if="role == 'admin' || role == 'superadmin' || role == 'Viewer'" class="flex">
                        <router-link to="/thinktank-management" class="hover:text-blue-700 transition font-medium">Think
                            Tank</router-link>
                        <span class="mx-1">/</span>
                    </div>
                    <span class="text-blue-900 font-semibold">Think Tank</span>
                </nav>

                <!-- Page Title Card -->
                <div class="bg-white sm:rounded-3xl shadow-md p-4 sm:p-8 mb-4 sm:mb-10 sm:border border-gray-200">
                    <h1 class="text-xl sm:text-3xl font-bold text-blue-900 flex items-center gap-3">
                        💡 Think Tank
                    </h1>
                    <p class="text-gray-500 mt-2 text-xs sm:text-sm">Share creative ideas to enhance institutional
                        growth and
                        collaboration.</p>
                </div>

                <!-- Dashboard Cards -->
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                    <!-- Total Think Tanks Card -->
                    <div class="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
                        <div class="flex items-center justify-between">
                            <div>
                                <p class="text-blue-100 text-sm font-medium">Total Think Tanks</p>
                                <p class="text-3xl font-bold mt-2">{{ formatNumber(thinkTankStatistics.totalThinkTanks)
                                }}</p>
                                <p class="text-blue-100 text-xs mt-1">Submitted Ideas</p>
                            </div>
                            <div class="bg-blue-400 bg-opacity-30 rounded-full p-3">
                                <font-awesome-icon :icon="['fas', 'lightbulb']" class="text-2xl" />
                            </div>
                        </div>
                    </div>

                    <!-- Budget Required Card -->
                    <div class="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
                        <div class="flex items-center justify-between">
                            <div>
                                <p class="text-green-100 text-sm font-medium">Budget Required</p>
                                <p class="text-3xl font-bold mt-2">{{
                                    formatNumber(thinkTankStatistics.budgetRequiredCount) }}</p>
                                <p class="text-green-100 text-xs mt-1">{{ getBudgetRequiredPercentage() }}% of total</p>
                            </div>
                            <div class="bg-green-400 bg-opacity-30 rounded-full p-3">
                                <font-awesome-icon :icon="['fas', 'coins']" class="text-2xl" />
                            </div>
                        </div>
                    </div>

                    <!-- Total Budget Card -->
                    <div class="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-lg p-6 text-white">
                        <div class="flex items-center justify-between">
                            <div>
                                <p class="text-orange-100 text-sm font-medium">Total Budget</p>
                                <p class="text-2xl font-bold mt-2">{{ formatCurrency(thinkTankStatistics.totalBudget) }}
                                </p>
                                <p class="text-orange-100 text-xs mt-1">AED Required</p>
                            </div>
                            <div class="bg-orange-400 bg-opacity-30 rounded-full p-3 flex-shrink-0">
                                <img class="h-6 w-6" src="../../assets/images/default/DirhamWhite.svg" alt="AED" />
                            </div>
                        </div>
                    </div>

                    <!-- Top Entity Card -->
                    <div class="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
                        <div class="flex items-center justify-between">
                            <div>
                                <p class="text-purple-100 text-sm font-medium">Top Entity</p>
                                <p class="text-2xl font-bold mt-2">{{ getTopEntity() }}</p>
                                <p class="text-purple-100 text-xs mt-1">{{ getTopEntityCount() }} think tanks</p>
                            </div>
                            <div class="bg-purple-400 bg-opacity-30 rounded-full p-3">
                                <font-awesome-icon :icon="['fas', 'building']" class="text-2xl" />
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Charts Section -->
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    <!-- Think Tanks by Entity Chart -->
                    <ThinkTanksByEntityChart :data="thinkTankStatistics.thinkTanksByEntity"
                        @entity-clicked="onEntityChartClick" />

                    <!-- Budget by Entity Chart -->
                    <BudgetByEntityChart :data="thinkTankStatistics.budgetByEntity"
                        @entity-clicked="onEntityChartClick" />
                </div>

                <!-- Advanced Search (after charts) -->
                <div class="px-4 sm:px-0 mb-4">
                    <ThinkTankAdvancedSearch :initial-basic-search="basicSearchText"
                        :initial-advanced-search="advancedSearch" @on-basic-search="onBasicSearch"
                        @on-advanced-search="onAdvancedSearch" @on-clear-search="clearSearch" />
                </div>

                <!-- Ideas Section -->
                <section
                    class="bg-white sm:rounded-3xl shadow-md p-0 sm:p-8 sm:border border-gray-200 flex-auto flex flex-col">
                    <div
                        class="flex justify-between items-center flex-wrap gap-4 sm:mb-6 p-4 sm:p-0 border-b-4 sm:border-b-0">
                        <h2 class="text-lg sm:text-2xl font-semibold text-blue-900">🎯 Submitted Ideas</h2>
                        <button @click="showForm = true"
                            class="flex items-center px-2 py-1 sm:px-5 sm:py-2.5 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-full shadow-lg hover:from-green-600 hover:to-green-700 transition text-xs sm:text-base">
                            <font-awesome-icon :icon="['fas', 'plus']" class="mr-2" />
                            Add Idea
                        </button>
                    </div>

                    <div v-if="hasItems" class="flex flex-col">
                        <div class="grid md:grid-cols-2 gap-0 sm:gap-6">
                            <!-- @click.stop="viewItem(item)" -->
                            <ThinkTankPostWithComment v-for="(item, index) in items" :key="index" :item="item"
                                :paginator-info="paginatorInfo" :index="index" :current-date-time="currentDateTime"
                                @view-item="viewItem" @toggle-like="toggleLike"
                                @update-comments-count="updateCommentsCount" />
                        </div>
                        <div class="mt-4 px-4 sm:px-0 pb-4 sm:pb-0">
                            <pagination :paginatorInfo="paginatorInfo" :setPage="setPage">
                            </pagination>
                        </div>
                    </div>
                    <div v-else class="flex-auto flex flex-col justify-center items-center py-12 px-6">
                        <div class="text-center max-w-md">
                            <div class="mb-6">
                                <div
                                    class="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center">
                                    <font-awesome-icon :icon="['fas', 'lightbulb']" class="h-12 w-12 text-gray-400" />
                                </div>
                            </div>
                            <h2 class="text-2xl font-semibold text-gray-800 mb-3">No Ideas Found</h2>
                            <p class="text-gray-500 mb-6 leading-relaxed">
                                We couldn't find any ideas matching your current search criteria.
                                Try adjusting your filters or search terms to find what you're looking for.
                            </p>
                            <div class="flex flex-col sm:flex-row gap-3 justify-center">
                                <button @click="clearFilters()"
                                    class="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200 text-sm font-medium">
                                    <font-awesome-icon :icon="['fas', 'filter']" class="h-4 w-4 mr-2" />
                                    Clear All Filters
                                </button>
                                <button @click="clearSearch()"
                                    class="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors duration-200 text-sm font-medium">
                                    <font-awesome-icon :icon="['fas', 'search']" class="h-4 w-4 mr-2" />
                                    Clear Search
                                </button>
                            </div>
                            <div class="mt-8 pt-6 border-t border-gray-200">
                                <p class="text-sm text-gray-400">
                                    <font-awesome-icon :icon="['fas', 'lightbulb']" class="h-4 w-4 mr-1" />
                                    Tip: Use the advanced search to find ideas by budget, date, or text.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                <transition name="fade">
                    <base-modal v-if="showForm" title="Submit a New Idea" @close="closeForm">
                        <form @submit.prevent="submitIdea" class="px-8 pt-3">
                            <div class="overflow-y-auto space-y-3">
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-1">Brief
                                        Description</label>
                                    <textarea v-model="form.short_description" rows="2"
                                        class="w-full mt-1 p-3 border border-gray-300 rounded-xl focus:outline-none"
                                        placeholder="Briefly explain your idea" required></textarea>
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-1">Full
                                        Description</label>
                                    <textarea v-model="form.description" rows="4"
                                        class="w-full mt-1 p-3 border border-gray-300 rounded-xl focus:outline-none"
                                        placeholder="Explain your idea in detail..." required></textarea>
                                </div>

                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-1">Is Budget
                                        Required?</label>
                                    <div class="flex gap-6 mt-2">
                                        <label class="inline-flex items-center">
                                            <input type="radio" v-model="form.budgetRequired" :value="true"
                                                class="mr-2" />
                                            Yes
                                        </label>
                                        <label class="inline-flex items-center">
                                            <input type="radio" v-model="form.budgetRequired" :value="false"
                                                class="mr-2" />
                                            No
                                        </label>
                                    </div>
                                </div>

                                <div v-if="form.budgetRequired">
                                    <label class="block text-sm font-medium text-gray-700 mb-1">Estimated Amount
                                        (AED)</label>
                                    <input v-model.number="form.estimatedAmount" type="number" min="0"
                                        class="w-full mt-1 p-3 border border-gray-300 rounded-xl focus:outline-none"
                                        placeholder="e.g. 5000" required />
                                </div>

                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-1">Attachments
                                        (Optional)</label>
                                    <input type="file" @change="handleFiles" multiple class="mt-2 text-sm" />
                                </div>
                            </div>

                            <div class="flex justify-end gap-3 mt-6">
                                <button type="button" @click="cancelSubmit"
                                    class="px-5 py-2.5 bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300 transition">
                                    Cancel
                                </button>
                                <button type="submit"
                                    class="px-5 py-2.5 bg-blue-600 text-white rounded-full shadow hover:bg-blue-700 transition">
                                    Submit Idea
                                </button>
                            </div>
                        </form>
                    </base-modal>
                </transition>

            </div>
        </div>

        <transition name="fade" mode="out-in">
            <base-full-modal v-if="isViewItem" :title="`View Think Tank`" height="" @close="closeModal">
                <think-tank-view class="text-left" :item="selectedItem" @close="closeModal"></think-tank-view>
            </base-full-modal>
        </transition>

        <div class="h-full flex justify-center items-center" v-if="isLoading">
            <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
        </div>

    </div>
</template>

<script>
import gql from "graphql-tag";
import Pagination from "../../components/shared/pagination.vue";
import ThinkTankView from "./view.vue";
import ThinkTankPostWithComment from "./components/ThinkTankPostWithComment.vue";
import ThinkTanksByEntityChart from "./charts/ThinkTanksByEntityChart.vue";
import BudgetByEntityChart from "./charts/BudgetByEntityChart.vue";
import { formatDateToServerFormat } from "../../helpers/utils";
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';
import { preloadSounds } from "../../helpers/soundUtils";
import ThinkTankAdvancedSearch from './components/ThinkTankAdvancedSearch.vue';

const LIST_THINK_TANKS_QUERY = gql`
  query ListThinkTanks($first: Int!, $page: Int!, $entities: [String!], $campuses: [String!], $departments: [String!], $userTypes: [String!], $person: Int, $search: String, $budget_required: Boolean, $amount_min: Float, $amount_max: Float, $date_from: Date, $date_to: Date) {
    listThinkTanks(first: $first, page: $page, entities: $entities, campuses: $campuses, departments: $departments, userTypes: $userTypes, person: $person, search: $search, budget_required: $budget_required, amount_min: $amount_min, amount_max: $amount_max, date_from: $date_from, date_to: $date_to) {
      error
      error_code
      message
      paginatorInfo {
        count
        currentPage
        firstItem
        hasMorePages
        lastItem
        lastPage
        perPage
        total
      }
      data {
        token
        user {
            token
            name
            email
            phone_number
            profile_picture_url
            entity
            campuses
        }
        short_description
        description
        budget_required
        estimated_amount
        created_at
        attachments {
          file_path
          file_name
        }
        likes_count
        liked_by_me
        comments_count
      }
    }
  }
`;

const CREATE_THINK_TANK_MUTATION = gql`
  mutation CreateThinkTank($input: ThinkTankInput!) {
    createThinkTank(input: $input) {
      error
      error_code
      message
    }
  }
`;

const TOGGLE_LIKE_THINK_TANK = gql`
  mutation ToggleLikeThinkTank($think_tank_token: String!) {
    toggleLikeThinkTank(think_tank_token: $think_tank_token) {
        error
        error_code
        message
        data {
            id
            likes_count
            liked_by_me
        }
    }
  }
`;

const GET_THINK_TANK_STATISTICS = gql`
  query GetThinkTankStatistics($entities: [String!], $campuses: [String!], $departments: [String!], $userTypes: [String!], $person: Int) {
    thinkTankStatistics(entities: $entities, campuses: $campuses, departments: $departments, userTypes: $userTypes, person: $person) {
      error
      error_code
      message
      data {
        totalThinkTanks
        budgetRequiredCount
        totalBudget
        thinkTanksByEntity {
          entity
          count
        }
        budgetByEntity {
          entity
          budget
        }
      }
    }
  }
`;

const GET_THINK_TANK_USER_TYPES = gql`
  query GetThinkTankUserTypes {
    thinkTankUserTypes {
      error
      error_code
      message
      data {
        role
        role_to_display
      }
    }
  }
`;

const GET_THINK_TANK_DEPARTMENTS = gql`
  query GetThinkTankDepartments($roles: [String!]) {
    thinkTankUserDepartments(roles: $roles) {
      error
      error_code
      message
      data {
        department
      }
    }
  }
`;

const GET_THINK_TANK_PERSONS = gql`
  query GetThinkTankPersons($roles: [String!], $departments: [String!]) {
    thinkTankPersons(roles: $roles, departments: $departments) {
      error
      error_code
      message
      data {
        user_id
        name
      }
    }
  }
`;

export default {
    name: "thinktank-index",
    components: {
        Pagination,
        ThinkTankView,
        ThinkTankPostWithComment,
        ThinkTanksByEntityChart,
        BudgetByEntityChart,
        Multiselect,
        ThinkTankAdvancedSearch,
    },
    data() {
        return {
            showFilter: false,
            basicSearchText: "",
            advancedSearch: {
                budget_required: null,
                amount_min: null,
                amount_max: null,
                date_from: "",
                date_to: "",
            },
            filters: {
                entities: [],
                campuses: [],
                userTypes: [],
                departments: [],
                person: null,
            },
            entityOptions: [
                { id: 'FCHS', name: 'FCHS' },
                { id: 'ADPOLY', name: 'ADPOLY' },
                { id: 'ATHS', name: 'ATS' },
                { id: 'ADVETI', name: 'ADVETI' },
            ],
            campusOptions: [],
            userTypeOptions: [],
            departmentOptions: [],
            personOptions: [],
            settings: [],
            currentDateTime: this.getFormatDateToServerFormat(new Date()),
            timeUpdater: null,
            first: 6,
            page: 1,
            isLoading: false,
            showForm: false,
            form: {
                short_description: "",
                description: "",
                budgetRequired: false,
                estimatedAmount: null,
                attachments: [],
            },
            isViewItem: false,
            selectedItem: null,
            thinkTankList: [],
            thinkTankStatistics: {
                totalThinkTanks: 0,
                budgetRequiredCount: 0,
                totalBudget: 0,
                thinkTanksByEntity: [],
                budgetByEntity: [],
            },
        };
    },
    created() {
        this.loadData();
        this.fetchThinkTankStatistics();
        // Preload sound files for better performance
        preloadSounds();
        this.fetchCampuses();
        this.fetchUserTypes();
        this.fetchDepartments();
        this.fetchPersons();
    },
    watch: {
        settings(val) {
            if (val) {
                this.$store.commit("settings/setSettings", val);

                let current = null;

                if (Array.isArray(val)) {
                    const found = val.find(setting => setting.name === "current_datetime");
                    if (found && found.value) {
                        current = found.value;
                    }
                }

                if (!current) {
                    const now = new Date();
                    current = this.getFormatDateToServerFormat(now);
                }

                this.currentDateTime = current;

                // Start the updater only after currentDateTime is set
                this.startCurrentDateTimeUpdater();
            }
        },
        'filters.entities': {
            handler() {
                // Reset campuses if they are not in the filtered list anymore
                const allowed = this.campusOptionsFiltered.map(c => c.id);
                this.filters.campuses = (this.filters.campuses || []).filter(c => allowed.indexOf(c.id) !== -1);
            },
            deep: true
        },
        'filters.userTypes': {
            handler() {
                // Clear departments and person when user types change
                this.filters.departments = [];
                this.filters.person = null;
                // Refetch departments and persons based on selected user types
                this.fetchDepartments();
                this.fetchPersons();
            },
            deep: true
        },
        'filters.departments': {
            handler() {
                // Clear person when departments change
                this.filters.person = null;
                // Refetch persons based on selected departments
                this.fetchPersons();
            },
            deep: true
        },
    },
    computed: {
        role() {
            return this.$store.getters.role;
        },
        items() {
            return this.thinkTankList.data;
        },
        hasItems() {
            if (this.items && this.items.length > 0) {
                return true;
            } else {
                return false;
            }
        },
        paginatorInfo() {
            return this.thinkTankList.paginatorInfo;
        },
        campusOptionsFiltered() {
            const selectedEntities = (this.filters.entities || []).map(e => e.id);
            const byEntity = selectedEntities.length
                ? this.campusOptions.filter(c => selectedEntities.indexOf(c.entity) !== -1)
                : this.campusOptions;
            return byEntity
                .filter(c => c.name && c.name !== '')
                .sort((a, b) => a.name.localeCompare(b.name));
        },
    },
    apollo: {
        settings() {
            return {
                query: gql`query GetSettings {
                    settings {
                        name
                        value
                    }
                }`,
                fetchPolicy: 'cache-and-network'
            }
        },
    },
    methods: {
        toggleFilter() {
            this.showFilter = !this.showFilter;
        },
        async loadData() {
            this.isLoading = true;
            try {
                const response = await this.$apollo.query({
                    query: LIST_THINK_TANKS_QUERY,
                    variables: {
                        first: this.first,
                        page: this.page,
                        entities: (this.filters.entities || []).map(e => e.id),
                        campuses: (this.filters.campuses || []).map(c => c.id),
                        departments: (this.filters.departments || []).map(d => d.id),
                        userTypes: (this.filters.userTypes || []).map(u => u.id),
                        person: this.filters.person ? this.filters.person.id : null,
                        search: this.basicSearchText || null,
                        budget_required: this.advancedSearch.budget_required,
                        amount_min: this.advancedSearch.amount_min,
                        amount_max: this.advancedSearch.amount_max,
                        date_from: this.advancedSearch.date_from || null,
                        date_to: this.advancedSearch.date_to || null,
                    },
                    fetchPolicy: 'network-only'
                });

                this.thinkTankList = response.data.listThinkTanks;

            } catch (error) {
                console.error("Failed to load think tank:", error);
            }
            this.isLoading = false;
        },
        clearFilters() {
            this.showFilter = false;
            this.filters = {
                entities: [],
                campuses: [],
                userTypes: [],
                departments: [],
                person: null,
            };
            this.basicSearchText = "";
            this.advancedSearch = {
                budget_required: null,
                amount_min: null,
                amount_max: null,
                date_from: "",
                date_to: "",
            };
            this.loadData();
            this.fetchThinkTankStatistics();
        },
        clearSearch() {
            this.basicSearchText = "";
            this.advancedSearch = {
                budget_required: null,
                amount_min: null,
                amount_max: null,
                date_from: "",
                date_to: "",
            };
            this.setPage(1);
        },
        onBasicSearch(text) {
            this.basicSearchText = text;
            this.setPage(1);
        },
        onAdvancedSearch(filters) {
            this.advancedSearch = { ...this.advancedSearch, ...filters };
            this.setPage(1);
        },
        applyFilters() {
            this.toggleFilter();
            this.loadData();
            this.fetchThinkTankStatistics();
        },
        setPage(page) {
            this.page = page;
            this.loadData();
        },
        handleFiles(event) {
            this.form.attachments = Array.from(event.target.files);
        },
        async submitIdea() {
            this.isLoading = true;

            try {
                const { data } = await this.$apollo.mutate({
                    mutation: CREATE_THINK_TANK_MUTATION,
                    variables: {
                        input: this.form
                    },
                });

                const response = data.createThinkTank;

                if (response.error) {
                    return this.$swal.fire({
                        icon: 'error',
                        title: 'Oops...',
                        text: response.message,
                    });
                }

                this.$swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: response.message,
                    showConfirmButton: false,
                    timer: 1000,
                    timerProgressBar: true,
                });

                this.loadData();
                this.fetchThinkTankStatistics();

            } catch (error) {
                this.$swal.fire({
                    icon: 'error',
                    title: 'Failed!',
                    text: error.message || 'Could not create Think Tank.',
                });
            } finally {
                this.isLoading = false;
            }

            this.resetForm();
            this.showForm = false;
        },
        cancelSubmit() {
            this.showForm = false;
            this.resetForm();
        },
        resetForm() {
            this.form = {
                short_description: "",
                description: "",
                budgetRequired: false,
                estimatedAmount: null,
                attachments: [],
            };
        },
        viewItem(item) {
            this.selectedItem = item;
            this.isViewItem = true;
        },
        closeModal() {
            this.isViewItem = false;
            this.selectedItem = null;
        },
        getFormatDateToServerFormat(date) {
            return formatDateToServerFormat(date);
        },
        startCurrentDateTimeUpdater() {
            if (this.timeUpdater) clearInterval(this.timeUpdater); // Clear old interval if exists

            this.timeUpdater = setInterval(() => {
                const updated = new Date(this.currentDateTime);
                updated.setMinutes(updated.getMinutes() + 1);
                this.currentDateTime = this.getFormatDateToServerFormat(updated);
            }, 60000); // Every 60 seconds
        },
        async toggleLike(token, index) {

            const originalItem = this.thinkTankList.data[index];
            const optimisticLiked = !originalItem.liked_by_me;
            const optimisticCount = optimisticLiked
                ? originalItem.likes_count + 1
                : originalItem.likes_count - 1;

            // Clone the data array and update the item
            const updatedData = [...this.thinkTankList.data];
            updatedData[index] = {
                ...originalItem,
                liked_by_me: optimisticLiked,
                likes_count: optimisticCount,
            };

            // Update state optimistically
            this.thinkTankList = {
                ...this.thinkTankList,
                data: updatedData,
            };

            try {
                const { data } = await this.$apollo.mutate({
                    mutation: TOGGLE_LIKE_THINK_TANK,
                    variables: {
                        think_tank_token: token
                    },
                });

                const updatedThinkTank = data.toggleLikeThinkTank.data;

                // Clone the array to prevent mutation of read-only Apollo response
                const updatedList = this.thinkTankList.data.map(item => {
                    if (item.token === token) {
                        return {
                            ...item,
                            liked_by_me: updatedThinkTank.liked_by_me,
                            likes_count: updatedThinkTank.likes_count
                        };
                    }
                    return item;
                });

                // Reassign the updated list
                this.thinkTankList = {
                    ...this.thinkTankList,
                    data: updatedList
                };

            } catch (error) {
                this.$swal.fire({
                    icon: 'error',
                    title: 'Failed!',
                    text: error.message || 'Could not like/unlike Think Tank.',
                });
            }
        },
        updateCommentsCount(token) {

            // Clone the array to prevent mutation of read-only Apollo response
            const updatedList = this.thinkTankList.data.map(item => {
                if (item.token === token) {
                    return {
                        ...item,
                        comments_count: (item.comments_count || 0) + 1
                    };
                }
                return item;
            });

            // Reassign the updated list
            this.thinkTankList = {
                ...this.thinkTankList,
                data: updatedList
            };

        },
        closeForm() {
            this.showForm = false;
            this.resetForm();
        },
        async fetchThinkTankStatistics() {
            try {
                const response = await this.$apollo.query({
                    query: GET_THINK_TANK_STATISTICS,
                    variables: {
                        entities: (this.filters.entities || []).map(e => e.id),
                        campuses: (this.filters.campuses || []).map(c => c.id),
                        departments: (this.filters.departments || []).map(d => d.id),
                        userTypes: (this.filters.userTypes || []).map(u => u.id),
                        person: this.filters.person ? this.filters.person.id : null,
                    },
                    fetchPolicy: "network-only",
                });

                if (response.data.thinkTankStatistics.error) {
                    console.error("GraphQL Error:", response.data.thinkTankStatistics.message);
                    return;
                }

                this.thinkTankStatistics = response.data.thinkTankStatistics.data;
            } catch (error) {
                console.error("Failed to load think tank statistics:", error);
            }
        },
        formatNumber(num) {
            return num.toLocaleString();
        },
        formatCurrency(amount) {
            if (amount === 0 || !amount) return '0';
            return new Intl.NumberFormat('en-AE', {
                style: 'currency',
                currency: 'AED',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            }).format(amount);
        },
        getTopEntity() {
            if (this.thinkTankStatistics.thinkTanksByEntity && this.thinkTankStatistics.thinkTanksByEntity.length > 0) {
                return this.thinkTankStatistics.thinkTanksByEntity[0].entity.replace(/ATHS/g, "ATS");
            }
            return "N/A";
        },
        getTopEntityCount() {
            if (this.thinkTankStatistics.thinkTanksByEntity && this.thinkTankStatistics.thinkTanksByEntity.length > 0) {
                return this.thinkTankStatistics.thinkTanksByEntity[0].count;
            }
            return 0;
        },
        getBudgetRequiredPercentage() {
            const budgetCount = this.thinkTankStatistics.budgetRequiredCount;
            const total = this.thinkTankStatistics.totalThinkTanks;
            if (total > 0) {
                return ((budgetCount / total) * 100).toFixed(1);
            }
            return "0.0";
        },
        onEntityChartClick(entity) {
            this.filters.entities = [{ id: entity, name: entity.replace(/ATHS/g, 'ATS') }];
            this.loadData();
            this.fetchThinkTankStatistics();
        },
        async fetchCampuses() {
            try {
                const { data } = await this.$apollo.query({
                    query: gql`query GetCampusesPublic {
                        campusesPublic {
                            error
                            message
                            data {
                                Entity
                                portal_campus
                            }
                        }
                    }`,
                    fetchPolicy: 'network-only'
                });

                if (data && data.campusesPublic && data.campusesPublic.data) {
                    const rows = data.campusesPublic.data;
                    this.campusOptions = rows
                        .filter(r => r && r.portal_campus)
                        .map(r => ({ id: r.portal_campus, name: r.portal_campus, entity: (r.Entity || '').trim() }));
                }
            } catch (error) {
                console.error('Failed to fetch campuses:', error);
            }
        },
        async fetchUserTypes() {
            try {
                const { data } = await this.$apollo.query({
                    query: GET_THINK_TANK_USER_TYPES,
                    fetchPolicy: 'network-only'
                });

                if (data && data.thinkTankUserTypes && data.thinkTankUserTypes.data) {
                    this.userTypeOptions = data.thinkTankUserTypes.data.map(userType => ({
                        id: userType.role,
                        name: userType.role_to_display
                    }));
                }
            } catch (error) {
                console.error('Failed to fetch user types:', error);
            }
        },
        async fetchDepartments() {
            try {
                const selectedRoles = (this.filters.userTypes || []).map(u => u.id);
                const { data } = await this.$apollo.query({
                    query: GET_THINK_TANK_DEPARTMENTS,
                    variables: {
                        roles: selectedRoles.length > 0 ? selectedRoles : null,
                    },
                    fetchPolicy: 'network-only'
                });

                if (data && data.thinkTankUserDepartments && data.thinkTankUserDepartments.data) {
                    this.departmentOptions = data.thinkTankUserDepartments.data.map(dept => ({
                        id: dept.department,
                        name: dept.department
                    }));
                }
            } catch (error) {
                console.error('Failed to fetch departments:', error);
            }
        },
        async fetchPersons() {
            try {
                const selectedRoles = (this.filters.userTypes || []).map(u => u.id);
                const selectedDepartments = (this.filters.departments || []).map(d => d.id);
                const { data } = await this.$apollo.query({
                    query: GET_THINK_TANK_PERSONS,
                    variables: {
                        roles: selectedRoles.length > 0 ? selectedRoles : null,
                        departments: selectedDepartments.length > 0 ? selectedDepartments : null,
                    },
                    fetchPolicy: 'network-only'
                });

                if (data && data.thinkTankPersons && data.thinkTankPersons.data) {
                    this.personOptions = data.thinkTankPersons.data.map(person => ({
                        id: person.user_id,
                        name: person.name
                    }));
                }
            } catch (error) {
                console.error('Failed to fetch persons:', error);
            }
        },
    },

    beforeUnmount() {
        if (this.timeUpdater) clearInterval(this.timeUpdater);
    },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
    transition: transform 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
    transform: translateX(-100%);
}

.ht-18 {
    height: 18px;
}
</style>
